import { axiosBaseQuery } from '@client';
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  HusbandryCostEstimatorBasicCalculationRequest,
  HusbandryCostEstimatorBasicGroupsResponse,
  HusbandryCostEstimatorBasicCalculation,
  HusbandryCostEstimatorBasicGroupAmountsResponse,
  HusbandryCostEstimatorAdvancedGroupsResponse,
  HusbandryCostEstimatorAdvancedCalculationRequest,
  HusbandryCostEstimatorAdvancedCalculation,
  HusbandryCostEstimatorAdvancedGroupAmountResponse,
  PriceListExpenseItemAverage,
  RefData
} from '../../types';

const compareBasicGroups = (
  group1: { groupId: number },
  group2: { groupId: number }
) => group1.groupId - group2.groupId;

const compareAdvancedGroups = (
  group1: HusbandryCostEstimatorAdvancedGroupsResponse[number],
  group2: HusbandryCostEstimatorAdvancedGroupsResponse[number]
) =>
  group1.expenseCategoryGroupDetails.id - group2.expenseCategoryGroupDetails.id;

import {
  createEntityAdapter,
  createSelector,
  EntityState
} from '@reduxjs/toolkit';

// Adapter for advance group categories
const advanacedGroupCategoryAdapter = createEntityAdapter({
  selectId: (group: HusbandryCostEstimatorAdvancedGroupsResponse[number]) =>
    group.expenseCategoryGroupDetails.id
});

export const { selectById: selectAdvancedGroupById } =
  advanacedGroupCategoryAdapter.getSelectors();

export const priceListExpenseAverageAdapter = createEntityAdapter<
  PriceListExpenseItemAverage & { groupId: number }
>({
  selectId: (expense) => `${expense.expenseId}_${expense.groupId}`
});

export const { selectById: selectPriceListExpenseAverageById } =
  priceListExpenseAverageAdapter.getSelectors();

export const husbandryCostEstimatorApi = createApi({
  reducerPath: 'husbandryCostEstimatorApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/secured/husbandry-cost-estimator' }),
  endpoints: (build) => ({
    fetchBasicHusbandryCostEstimatorGroups: build.query<
      HusbandryCostEstimatorBasicGroupsResponse,
      void
    >({
      query: () => ({ url: '/get-basic-groups', method: 'GET' }),
      transformResponse: (res: HusbandryCostEstimatorBasicGroupsResponse) =>
        res.sort(compareBasicGroups)
    }),
    performBasicCalculation: build.mutation<
      HusbandryCostEstimatorBasicCalculation,
      HusbandryCostEstimatorBasicCalculationRequest
    >({
      query: (data) => ({
        url: '/calculate',
        method: 'POST',
        data
      }),
      transformResponse: (
        res: HusbandryCostEstimatorBasicGroupAmountsResponse
      ) => ({
        groupCalculations: res.sort(compareBasicGroups),
        total: res.reduce((acc, group) => acc + group.amount, 0)
      })
    }),

    fetchAdvancedHusbandryCostEstimatorGroups: build.query<
      {
        defaultGroupsResponse: HusbandryCostEstimatorAdvancedGroupsResponse;
        defaultGroupsEntity: EntityState<
          HusbandryCostEstimatorAdvancedGroupsResponse[number]
        >;
      },
      void
    >({
      query: () => ({ url: '/advanced/get-advanced-group', method: 'GET' }),
      transformResponse: (
        response: HusbandryCostEstimatorAdvancedGroupsResponse
      ) => {
        const advanacedExpenseCategoryEntityState =
          advanacedGroupCategoryAdapter.setAll(
            advanacedGroupCategoryAdapter.getInitialState(),
            response
          );

        return {
          defaultGroupsResponse: response.sort(compareAdvancedGroups),
          defaultGroupsEntity: advanacedExpenseCategoryEntityState
        };
      }
    }),
    performAdvancedCalculation: build.mutation<
      HusbandryCostEstimatorAdvancedCalculation,
      HusbandryCostEstimatorAdvancedCalculationRequest
    >({
      query: (data) => ({
        url: '/advance/calculate',
        method: 'POST',
        data
      }),
      transformResponse: (
        res: HusbandryCostEstimatorAdvancedGroupAmountResponse
      ) => {
        const groups = res.groups ?? [];
        const expenseAverages = (groups ?? []).reduce<
          (PriceListExpenseItemAverage & { groupId: number })[]
        >((acc, group) => {
          acc.push(
            ...(group.expenseItemAvgPriceList ?? []).map((expense) => ({
              ...expense,
              groupId: group.groupId
            }))
          );
          return acc;
        }, []);

        const expenseAverageEntityState = priceListExpenseAverageAdapter.setAll(
          priceListExpenseAverageAdapter.getInitialState(),
          expenseAverages
        );

        return {
          portId: res.portId,
          groups: (groups ?? []).sort(compareBasicGroups),
          total: (groups ?? []).reduce(
            (acc, group) => acc + group.groupTotal,
            0
          ),
          source: res.source,
          expenseAverageEntityState
        };
      }
    }),
    loadPortsWithPriceList: build.query<RefData[], { filter: string }>({
      query: ({ filter }) => ({
        url: `/filter/ports`,
        method: 'GET',
        params: { filter }
      })
    })
  })
});

export const {
  useFetchBasicHusbandryCostEstimatorGroupsQuery,
  useFetchAdvancedHusbandryCostEstimatorGroupsQuery,
  usePerformBasicCalculationMutation,
  usePerformAdvancedCalculationMutation,
  useLazyLoadPortsWithPriceListQuery
} = husbandryCostEstimatorApi;

export const advancedCalculationMutationInitiator =
  husbandryCostEstimatorApi.endpoints.performAdvancedCalculation.initiate;

export const selectAllMutations = createSelector(
  [
    (state: {
      [husbandryCostEstimatorApi.reducerPath]: ReturnType<
        typeof husbandryCostEstimatorApi.reducer
      >;
    }) => state.husbandryCostEstimatorApi.mutations
  ],
  (mutations) =>
    Object.entries(mutations).reduce((acc, [key, mutation]) => {
      if (!mutation || !mutation.data) return acc;

      acc[key] = mutation.data as HusbandryCostEstimatorAdvancedCalculation;
      return acc;
    }, {} as Record<string, HusbandryCostEstimatorAdvancedCalculation>)
);
